import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useCompanyContext } from './use-company-context';

export interface FleetAlert {
  id: string;
  company_id: string;
  alert_type: string;
  priority: string;
  status: string;
  title: string;
  description?: string;
  vehicle_id?: string;
  driver_id?: string;
  source_table?: string;
  source_id?: string;
  metadata?: any;
  triggered_at: string;
  acknowledged_at?: string;
  acknowledged_by?: string;
  resolved_at?: string;
  resolved_by?: string;
  created_at: string;
  updated_at: string;
  fleet_vehicles?: {
    plate: string;
    make?: string;
    model?: string;
  } | null;
  fleet_drivers?: {
    name: string;
  } | null;
}

export function useFleetAlerts(companyIdParam?: string) {
  const { companyId: contextCompanyId } = useCompanyContext();
  const companyId = companyIdParam || contextCompanyId;
  
  console.log('🚚 [useFleetAlerts] companyId:', companyId);
  
  return useQuery({
    queryKey: ['fleet-alerts', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const { data, error } = await supabase
        .from('fleet_alerts')
        .select(`
          *,
          fleet_vehicles!vehicle_id (
            plate,
            make,
            model
          ),
          fleet_drivers!driver_id (
            name
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(item => ({
        ...item,
        fleet_vehicles: item.fleet_vehicles as any,
        fleet_drivers: item.fleet_drivers as any,
      })) as FleetAlert[];
    },
    enabled: !!companyId,
  });
}

export function useAcknowledgeAlert(companyId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (alertId: string) => {
      const { data, error } = await supabase
        .from('fleet_alerts')
        .update({
          status: 'acknowledged',
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .eq('id', alertId)
        .eq('company_id', companyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-alerts', companyId] });
      toast({
        title: "Alerta reconhecido",
        description: "O alerta foi marcado como reconhecido.",
      });
    },
    onError: (error) => {
      console.error('Error acknowledging alert:', error);
      toast({
        variant: "destructive",
        title: "Erro ao reconhecer alerta",
        description: "Não foi possível reconhecer o alerta.",
      });
    },
  });
}

export function useResolveAlert(companyId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (alertId: string) => {
      const { data, error } = await supabase
        .from('fleet_alerts')
        .update({
          status: 'resolved',
          resolved_at: new Date().toISOString(),
          resolved_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .eq('id', alertId)
        .eq('company_id', companyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-alerts', companyId] });
      toast({
        title: "Alerta resolvido",
        description: "O alerta foi marcado como resolvido.",
      });
    },
    onError: (error) => {
      console.error('Error resolving alert:', error);
      toast({
        variant: "destructive",
        title: "Erro ao resolver alerta",
        description: "Não foi possível resolver o alerta.",
      });
    },
  });
}

export function useCreateAlert(companyId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (alertData: Omit<FleetAlert, 'id' | 'created_at' | 'updated_at' | 'company_id' | 'fleet_vehicles' | 'fleet_drivers'>) => {
      const { data, error } = await supabase
        .from('fleet_alerts')
        .insert({
          ...alertData,
          company_id: companyId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-alerts', companyId] });
      toast({
        title: "Alerta criado",
        description: "Alerta foi criado com sucesso.",
      });
    },
    onError: (error) => {
      console.error('Error creating alert:', error);
      toast({
        variant: "destructive",
        title: "Erro ao criar alerta",
        description: "Não foi possível criar o alerta.",
      });
    },
  });
}